import { useEffect, useState, type ReactElement } from 'react';
import { vorZeit } from '@schnitt/shared';
import { api, type VerlaufSchritt } from '../api';
import { useEditor } from '../store';
import { Sheet } from './Sheet';

/**
 * Wer hat wann was geändert — und der Weg zurück.
 *
 * Das Protokoll gibt es seit dem ersten Tag, gelesen hat es nur nie jemand.
 * Hier steht es: neueste Änderung oben, mit Fassung, Person und Zeit.
 *
 * Der Weg zurück schreibt **nach vorn**: „zu dieser Fassung“ wirft die
 * späteren nicht weg, sondern legt den alten Stand als neue Fassung an. Damit
 * lässt sich auch der Rückweg wieder zurückgehen — ein Verlauf, der beim
 * Zurückgehen Löcher bekommt, ist keiner.
 */
export function VerlaufSheet({ onClose, closeLabel }: { onClose: () => void; closeLabel?: string }): ReactElement {
  const project = useEditor((s) => s.project);
  const role = useEditor((s) => s.role);
  const toast = useEditor((s) => s.toast);
  const [schritte, setSchritte] = useState<VerlaufSchritt[] | null>(null);
  const [rev, setRev] = useState(0);
  const [zurueckMoeglich, setZurueckMoeglich] = useState(false);
  const [frage, setFrage] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Ein Fehlschlag beim Laden muss sichtbar sein (7.78).
   *
   * Hier stand `.catch(() => null); if (!daten) return;`. Damit blieb das
   * Blatt bei „Einen Moment…“ stehen -- für immer, ohne ein Wort und ohne
   * einen Weg, es noch einmal zu versuchen. Der Browser-Durchgang ist bei
   * einem Zehntel der Wartezeiten genau darauf gelaufen und wartete
   * fünfzehn Sekunden auf eine Liste, die nie kommen konnte.
   *
   * Es ist dieselbe Lehre wie bei den Hinweisen (8. September, Saschas
   * „Hinweise werden nicht gespeichert und auch nicht angezeigt“): ein
   * Fangarm darf sein, aber er darf nichts verschweigen.
   */
  const [fehler, setFehler] = useState(false);

  const laden = async (): Promise<void> => {
    if (!project) return;
    setFehler(false);
    try {
      const daten = await api.verlauf(project.id);
      setSchritte(daten.schritte);
      setRev(daten.rev);
      setZurueckMoeglich(daten.zurueckMoeglich);
    } catch {
      setFehler(true);
    }
  };

  useEffect(() => {
    void laden();
    // Einmal beim Öffnen: ein Verlauf, der sich unter der Hand ändert,
    // während man ihn liest, ist schwerer zu lesen als einer, der steht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const zurueck = async (ziel: number): Promise<void> => {
    if (!project) return;
    setBusy(true);
    try {
      const { rev: neu } = await api.verlaufZurueck(project.id, ziel);
      toast('success', `Zurück auf Fassung ${ziel} — als neue Fassung ${neu}. Nichts ist verloren.`);
      setFrage(null);
      await laden();
    } catch (fehler) {
      toast('error', fehler instanceof Error ? fehler.message : 'Das ging nicht.');
    } finally {
      setBusy(false);
    }
  };

  const darf = role !== 'viewer' && zurueckMoeglich;
  return (
    <Sheet title="Verlauf" onClose={onClose} closeLabel={closeLabel}>
      <p className="hint" style={{ marginTop: 0 }}>
        Der Schnitt steht bei Fassung {rev}. Jede Änderung ist eine Fassung — neueste zuerst.
        {zurueckMoeglich
          ? ' Der alte Stand kommt als neue Fassung obendrauf.'
          : ' Zurückgehen geht hier nicht: das Protokoll ist unvollständig.'}
      </p>

      {fehler ? (
        <div className="fehler-zeile" data-verlauf-fehler>
          <span>Der Verlauf konnte nicht geladen werden. Er ist nicht weg — nur nicht hier.</span>
          <button className="ghost small" data-verlauf-nochmal onClick={() => void laden()}>
            Nochmal versuchen
          </button>
        </div>
      ) : schritte === null ? (
        <p className="hint laedt">Einen Moment…</p>
      ) : schritte.length === 0 ? (
        <p className="hint">Noch nichts geändert.</p>
      ) : (
        <div className="list" data-verlauf>
          {schritte.map((s) => (
            <div className="row" key={s.rev} data-schritt={s.rev}>
              <div className="grow">
                <div className="title">{s.label}</div>
                <div className="sub">
                  Fassung {s.rev} · {s.actor ?? 'die App'} · {vorZeit(s.createdAt)}
                </div>
              </div>
              {darf && s.rev < rev ? (
                frage === s.rev ? (
                  <div className="row" style={{ gap: 6 }}>
                    <button className="ghost small" disabled={busy} onClick={() => setFrage(null)}>
                      Doch nicht
                    </button>
                    <button className="primary small" disabled={busy} data-zurueck onClick={() => void zurueck(s.rev)}>
                      Wirklich zurück
                    </button>
                  </div>
                ) : (
                  <button className="ghost small" onClick={() => setFrage(s.rev)}>
                    Hierhin zurück
                  </button>
                )
              ) : null}
            </div>
          ))}
        </div>
      )}
    </Sheet>
  );
}
